// Regra de senha forte — compartilhada no front (espelhada no back em
// api/routes/usuarios.ts). Usada na troca de senha do usuário e no
// criar/resetar do admin.
export const SENHA_MIN = 8;

// Senhas óbvias/padrão que não devem passar (inclui o exemplo do seed).
const FRACAS = new Set([
  "trocar123",
  "mudar123",
  "senha123",
  "12345678",
  "123456789",
  "password",
  "m5group",
]);

/** Retorna a mensagem de erro, ou null se a senha for forte o suficiente. */
export function validarSenhaForte(s: string): string | null {
  if (s.length < SENHA_MIN) return `Use pelo menos ${SENHA_MIN} caracteres.`;
  if (!/[A-Za-zÀ-ÿ]/.test(s)) return "Inclua pelo menos uma letra.";
  if (!/[0-9]/.test(s)) return "Inclua pelo menos um número.";
  if (FRACAS.has(s.toLowerCase())) return "Essa senha é muito comum — escolha outra.";
  return null;
}

export const SENHA_DICA = `Mínimo ${SENHA_MIN} caracteres, com pelo menos uma letra e um número.`;

/** Sorteio uniforme em [0, n) usando o gerador criptográfico do navegador. */
function sorteio(n: number): number {
  // 🔑 Rejeição, não `% n`: o resto puxaria os primeiros índices do alfabeto,
  // e senha com letra mais provável que outra é senha com menos entropia do
  // que aparenta ter.
  const teto = Math.floor(256 / n) * n;
  const b = new Uint8Array(1);
  for (;;) {
    crypto.getRandomValues(b);
    if (b[0] < teto) return b[0] % n;
  }
}

/**
 * Gera uma senha provisória de 12 caracteres, sem os símbolos ambíguos
 * (I/O/0/1/l) — pra ninguém errar ao ditar. Era copiado IGUAL em Equipe.tsx e
 * Usuarios.tsx; agora mora aqui, um lugar só.
 *
 * 🟥 O DEFEITO (31/08, achado pelo dono ao criar um acesso de verdade): o
 * botão "Gerar" produzia senha que o PRÓPRIO formulário recusava, e o
 * "Criar acesso" ficava morto sem dizer por quê. A senha da vez —
 * `NGsWSfWdDrsU` — tinha 12 letras e NENHUM número, e a regra ao lado exigia
 * "pelo menos um número".
 *
 * Não era azar raro: o alfabeto tem 55 caracteres, 8 deles dígitos, então
 * `(47/55)^12` ≈ **15%** das senhas nasciam recusadas. Uma em cada seis ou
 * sete tentativas travava a tela.
 *
 * 🔑 O gerador sorteava sem conhecer a regra do validador. Agora ele CONSTRÓI
 * o que `validarSenhaForte` exige — um dígito e uma letra garantidos — em vez
 * de torcer para o sorteio cair certo. A régua liga os dois: mil senhas
 * geradas, mil aceitas.
 */
export function gerarSenha(): string {
  const LETRAS = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz";
  const DIGITOS = "23456789";
  const TODOS = LETRAS + DIGITOS;

  // Uma letra e um dígito entram por construção; o resto é livre.
  const chars = [LETRAS[sorteio(LETRAS.length)], DIGITOS[sorteio(DIGITOS.length)]];
  while (chars.length < 12) chars.push(TODOS[sorteio(TODOS.length)]);

  // Embaralha (Fisher-Yates), senão a letra e o dígito obrigatórios ficariam
  // SEMPRE nas duas primeiras posições — um padrão previsível na senha.
  for (let i = chars.length - 1; i > 0; i--) {
    const j = sorteio(i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join("");
}
